/**
 * Copyright © 2025-2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { CartProvider } from "@farmlands/commerce";
import React from "react";
import { useSearchParams } from "react-router-dom";

import Header from "./components/layout/Header";
import { BackgroundProvider } from "./contexts/BackgroundContext";
import { AccountSwitcherProvider } from "./features/azure-b2c/components/AccountSwitcherProvider";
import { PickUpStoreModalProvider } from "./features/cart/components/PickUpStoreModal";
import AddToCartProvider from "./features/cart/components/useAddToCartModal";
import DrawdownProvider from "./features/drawdown/v1/useDrawdownRequest";
import { PRODUCTS_CART_ROUTE, PRODUCTS_ROUTES } from "./features/products/constants";
import { ShopProvider } from "./features/shop/context/ShopProvider";
import AtomicProvider from "./services/atomic/AtomicProvider";
import { PageLayout } from "./AppPage";

const CART_PAGE_URL = `${
  process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI?.endsWith("/")
    ? process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI.slice(0, -1)
    : (process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI ?? "")
}${PRODUCTS_CART_ROUTE}`;

export const AppProviders: React.FC<{
  children: React.ReactNode;
  ready: boolean;
  fallback: React.ReactNode;
}> = (props) => {
  const [searchParams] = useSearchParams();

  if (props.ready) {
    return (
      <AccountSwitcherProvider>
        <AtomicProvider>
          <PickUpStoreModalProvider>
            <DrawdownProvider>
              <BackgroundProvider>
                <ShopProvider
                  productListUrl={`${PRODUCTS_ROUTES.BASE}?${searchParams.toString()}`}
                  cartPageUrl={CART_PAGE_URL}
                >
                  <CartProvider>
                    <AddToCartProvider>
                      <Header />
                      <PageLayout>{props.children}</PageLayout>
                    </AddToCartProvider>
                  </CartProvider>
                </ShopProvider>
              </BackgroundProvider>
            </DrawdownProvider>
          </PickUpStoreModalProvider>
        </AtomicProvider>
      </AccountSwitcherProvider>
    );
  } else {
    return props.fallback;
  }
};

export default AppProviders;
